import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Lock, MoveHorizontal, Play } from "lucide-react";
import { projects } from "../data/portfolio";
import ProjectCarousel from "./ProjectCarousel";

const ease = [0.16, 1, 0.3, 1];

function BigBackdropNumber({ scrollYProgress, label, side = "right" }) {
  const y = useTransform(scrollYProgress, [0, 1], [180, -180]);
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [0, 0.045, 0.045, 0]
  );
  return (
    <motion.div
      aria-hidden
      style={{ y, opacity }}
      className={`pointer-events-none absolute top-1/2 -translate-y-1/2 font-display text-[40vw] md:text-[22vw] lg:text-[18vw] leading-none tracking-[-0.06em] text-ink select-none ${
        side === "right" ? "right-[-2vw]" : "left-[-2vw]"
      }`}
    >
      {label}
    </motion.div>
  );
}

function ProjectBadges({ project, theme = "dark" }) {
  return (
    <>
      <div className="pointer-events-none absolute top-6 left-6 z-30 flex items-center gap-2">
        <span
          className={`font-display text-xs tracking-[0.3em] uppercase ${
            theme === "dark"
              ? "text-white/85 [text-shadow:0_1px_8px_rgba(0,0,0,0.6)]"
              : "text-ink"
          }`}
        >
          Case {project.index}
        </span>
      </div>
      <div className="pointer-events-none absolute top-6 right-6 z-30">
        {project.confidential ? (
          <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-black/55 backdrop-blur-md border border-white/10 text-[10px] font-body tracking-[0.18em] uppercase text-white/90">
            <Lock className="w-3 h-3" /> Enterprise
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-white/95 text-black text-[10px] font-body tracking-[0.18em] uppercase">
            Open Source
          </span>
        )}
      </div>
    </>
  );
}

function VideoFrame({ project }) {
  const isEmbed = project.video && project.video.startsWith("http");
  return (
    <div className="relative h-full w-full overflow-hidden rounded-2xl border border-line/5 bg-black shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]">
      {isEmbed ? (
        <iframe
          src={project.video}
          title={`${project.name} demo`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 z-10 h-full w-full border-0"
          data-testid={`project-${project.id}-video`}
        />
      ) : (
        <video
          src={project.video}
          poster={project.image}
          controls
          playsInline
          preload="metadata"
          className="absolute inset-0 z-10 h-full w-full object-cover"
          data-testid={`project-${project.id}-video`}
        />
      )}
      <div className="pointer-events-none absolute inset-0 z-20 rounded-2xl ring-1 ring-inset ring-white/5" />
      <ProjectBadges project={project} />
    </div>
  );
}

function CarouselFrame({ project }) {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-2xl border border-line/5 bg-surface-2 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]">
      <ProjectCarousel project={project} />
      <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/5" />
    </div>
  );
}

function CompactMobileFooter({ project }) {
  return (
    <div className="md:hidden flex items-center justify-between">
      <p className="font-body text-[10px] tracking-[0.4em] uppercase text-ink-2">
        Project {project.index} · {project.name}
      </p>
      {project.githubUrl && (
        <a
          href={project.githubUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`project-${project.id}-github-mobile`}
          aria-label="View on GitHub"
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full bg-ink text-surface font-body text-[11px] font-medium active:scale-95 transition-transform"
        >
          GitHub
          <ArrowUpRight className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

function FullDescription({ project, compact = false }) {
  const hasVideo = Boolean(project.video);

  if (compact) {
    return (
      <div>
        <p className="font-body text-[10px] tracking-[0.4em] uppercase text-ink-2">
          Project {project.index}
        </p>
        <h2 className="mt-3 font-display text-3xl tracking-[-0.03em] leading-[1.05] text-ink">
          {project.name}
        </h2>
        <p className="mt-3 font-display text-base text-ink-metal max-w-md leading-snug">
          {project.subtitle}
        </p>

        {hasVideo ? (
          <div className="mt-5">
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-2 mb-1.5">
              Outcome
            </p>
            <p className="font-body text-sm text-ink-metal/90 leading-relaxed">
              {project.outcome}
            </p>
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-2 text-ink-2">
            <MoveHorizontal className="w-3.5 h-3.5" />
            <p className="font-body text-[10px] tracking-[0.3em] uppercase">
              Swipe the case study to explore
            </p>
          </div>
        )}

        {project.githubUrl && (
          <a
            href={project.githubUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`project-${project.id}-github`}
            className="mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-full bg-ink text-surface font-body text-sm font-medium hover:bg-ink-metal transition-all duration-300 active:scale-95"
          >
            View on GitHub
            <ArrowUpRight className="w-4 h-4" />
          </a>
        )}

        {hasVideo && (
          <div className="mt-5 inline-flex items-center gap-2 text-ink-2">
            <Play className="w-3.5 h-3.5" />
            <p className="font-body text-[10px] tracking-[0.3em] uppercase">
              Scroll for the demo
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <p className="font-body text-[10px] tracking-[0.4em] uppercase text-ink-2">
        Project {project.index}
      </p>
      <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-[-0.03em] leading-[1.05] text-ink">
        {project.name}
      </h2>
      <p className="mt-3 font-display text-base md:text-xl text-ink-metal max-w-md leading-snug">
        {project.subtitle}
      </p>

      {hasVideo ? (
        <div className="mt-6 space-y-4 md:mt-7">
          <div>
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-2 mb-1.5">
              Problem
            </p>
            <p className="font-body text-sm md:text-base text-ink-metal/90 leading-relaxed">
              {project.problem}
            </p>
          </div>
          <div>
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-2 mb-1.5">
              Approach
            </p>
            <p className="font-body text-sm md:text-base text-ink-2 leading-relaxed">
              {project.approach}
            </p>
          </div>
          <div>
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-2 mb-1.5">
              Outcome
            </p>
            <p className="font-body text-sm md:text-base text-ink-metal/90 leading-relaxed">
              {project.outcome}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-6 flex items-center gap-2 text-ink-2">
          <MoveHorizontal className="w-3.5 h-3.5" />
          <p className="font-body text-[10px] tracking-[0.3em] uppercase">
            Swipe the case study to explore
          </p>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        {project.stack.map((s) => (
          <span
            key={s}
            className="inline-flex items-center px-3 h-7 rounded-full border border-line/10 bg-line/[0.02] font-body text-[11px] text-ink-metal tracking-wide"
          >
            {s}
          </span>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 max-w-md">
        {project.metrics.map((m, i) => (
          <div
            key={i}
            className="rounded-xl border border-line/5 bg-line/[0.02] px-4 py-3"
          >
            <div className="font-display text-xl text-metal tracking-tight">
              {m.v}
            </div>
            <div className="font-body text-[11px] text-ink-2 mt-0.5">
              {m.l}
            </div>
          </div>
        ))}
      </div>

      {project.githubUrl && (
        <a
          href={project.githubUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`project-${project.id}-github`}
          className="mt-7 inline-flex items-center gap-2 px-5 h-11 rounded-full bg-ink text-surface font-body text-sm font-medium hover:bg-ink-metal transition-all duration-300 active:scale-95"
        >
          View on GitHub
          <ArrowUpRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

function VideoProjectBlock({ project, idx }) {
  const desktopRef = useRef(null);
  const { scrollYProgress: desktopProgress } = useScroll({
    target: desktopRef,
    offset: ["start end", "end start"],
  });
  const desktopFrameY = useTransform(desktopProgress, [0, 1], [50, -50]);
  const desktopTextY = useTransform(desktopProgress, [0, 1], [40, -40]);
  const reverse = idx % 2 === 1;

  const mobileDescRef = useRef(null);
  const { scrollYProgress: mobileDescProgress } = useScroll({
    target: mobileDescRef,
    offset: ["start end", "end start"],
  });
  const mobileVideoRef = useRef(null);
  const { scrollYProgress: mobileVideoProgress } = useScroll({
    target: mobileVideoRef,
    offset: ["start end", "end start"],
  });
  const mobileVideoScale = useTransform(
    mobileVideoProgress,
    [0, 0.5, 1],
    [0.95, 1, 0.95]
  );

  return (
    <>
      {/* DESKTOP — single snap with side-by-side layout */}
      <article
        ref={desktopRef}
        data-testid={`project-${project.id}`}
        data-snap-page
        className="hidden md:flex relative h-[100svh] items-center overflow-hidden"
      >
        <BigBackdropNumber
          scrollYProgress={desktopProgress}
          label={project.index}
          side={reverse ? "left" : "right"}
        />
        <div className="relative w-full max-w-[1400px] mx-auto px-10">
          <div className="grid grid-cols-12 gap-x-8 items-center">
            <motion.div
              style={{ y: desktopFrameY }}
              className={`col-span-7 ${reverse ? "order-2 col-start-6" : ""}`}
            >
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.1, ease }}
                className="relative w-full aspect-[5/6] max-h-[78vh] mx-auto"
              >
                <VideoFrame project={project} />
              </motion.div>
            </motion.div>

            <motion.div
              style={{ y: desktopTextY }}
              className={`col-span-5 ${reverse ? "order-1 col-start-1" : ""}`}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease, delay: 0.1 }}
              >
                <FullDescription project={project} />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </article>

      {/* MOBILE — snap 1: compact description (fits one viewport) */}
      <article
        ref={mobileDescRef}
        data-testid={`project-${project.id}-info`}
        data-snap-page
        className="md:hidden relative h-[100svh] overflow-hidden"
      >
        <BigBackdropNumber
          scrollYProgress={mobileDescProgress}
          label={project.index}
          side={reverse ? "left" : "right"}
        />
        <div className="relative h-full px-6 pt-24 pb-10 flex flex-col justify-center">
          <FullDescription project={project} compact />
        </div>
      </article>

      {/* MOBILE — snap 2: the demo video */}
      <article
        ref={mobileVideoRef}
        data-testid={`project-${project.id}-demo`}
        data-snap-page
        className="md:hidden relative h-[100svh] flex items-center overflow-hidden"
      >
        <div className="absolute top-20 left-0 right-0 z-10 px-6 flex items-center justify-between">
          <span className="font-body text-[10px] tracking-[0.4em] uppercase text-ink-2">
            Case {project.index} · Demo
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-line/[0.04] border border-line/10 text-[10px] font-body tracking-[0.18em] uppercase text-ink-metal">
            <Play className="w-3 h-3" /> Live
          </span>
        </div>
        <motion.div
          style={{ scale: mobileVideoScale }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease }}
          className="relative w-full px-4"
        >
          <div className="relative aspect-[9/16] max-h-[78vh] w-full mx-auto">
            <VideoFrame project={project} />
          </div>
        </motion.div>
      </article>
    </>
  );
}

function CarouselProjectBlock({ project, idx }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const frameY = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const frameScale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.97, 1, 0.97]
  );
  const textY = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const reverse = idx % 2 === 1;

  return (
    <article
      ref={ref}
      data-testid={`project-${project.id}`}
      data-snap-page
      className="relative h-[100svh] flex items-center overflow-hidden"
    >
      <BigBackdropNumber
        scrollYProgress={scrollYProgress}
        label={project.index}
        side={reverse ? "left" : "right"}
      />

      <div className="relative w-full max-w-[1400px] mx-auto px-4 md:px-10 py-20 md:py-24 h-full">
        <div className="flex flex-col gap-y-6 h-full md:grid md:grid-cols-12 md:gap-x-8 md:gap-y-0 md:items-center">
          <div
            className={`flex-1 min-h-0 md:flex-none md:col-span-7 ${
              reverse ? "md:order-2 md:col-start-6" : ""
            }`}
          >
            <motion.div
              style={{ y: frameY, scale: frameScale }}
              className="relative w-full h-full md:h-auto md:aspect-[5/6] md:max-h-[78vh]"
            >
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.1, ease }}
                className="w-full h-full"
              >
                <CarouselFrame project={project} />
              </motion.div>
            </motion.div>
          </div>

          <motion.div
            style={{ y: textY }}
            className={`md:col-span-5 ${
              reverse ? "md:order-1 md:col-start-1" : ""
            }`}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease, delay: 0.1 }}
            >
              <CompactMobileFooter project={project} />
              <div className="hidden md:block">
                <FullDescription project={project} />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </article>
  );
}

function ProjectBlock({ project, idx }) {
  if (project.video) {
    return <VideoProjectBlock project={project} idx={idx} />;
  }
  return <CarouselProjectBlock project={project} idx={idx} />;
}

export default function Projects() {
  const headerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: headerRef,
    offset: ["start end", "end start"],
  });
  const headerY = useTransform(scrollYProgress, [0, 1], [60, -60]);

  return (
    <section
      data-testid="projects-section"
      className="relative bg-surface"
    >
      <div
        id="work"
        ref={headerRef}
        data-snap-page
        className="relative h-[100svh] flex items-center px-6 md:px-10 overflow-hidden"
      >
        <div className="max-w-[1400px] mx-auto w-full">
          <motion.div style={{ y: headerY }} className="grid grid-cols-12">
            <div className="col-span-12 md:col-span-3">
              <p className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2">
                03 — Selected work
              </p>
            </div>
            <div className="col-span-12 md:col-span-9 md:col-start-4 mt-6 md:mt-0">
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[1.05] text-ink max-w-[20ch]">
                Eight projects.<br />
                One throughline:{" "}
                <span className="text-metal">make the work disappear.</span>
              </h2>
              <p className="mt-6 font-body text-base md:text-lg text-ink-2 max-w-2xl leading-relaxed">
                A mix of enterprise systems behind the firewall and open-source
                work in the wild — each chosen to show a different shape of
                problem. One scroll lands you on the next case study; swipe
                within to walk through its slides.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="divide-y divide-line/5">
        {projects.map((p, i) => (
          <ProjectBlock key={p.id} project={p} idx={i} />
        ))}
      </div>
    </section>
  );
}
